import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Typography,
  Input,
  Button,
  Form,
  Select,
  Alert,
  Space,
  Tooltip,
  Progress,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  BankOutlined,
  CreditCardOutlined,
  LockOutlined,
  CameraOutlined,
  InfoCircleOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { getMsg } from "../../utils/antdStatic";
import "./perfil.css";
import { usePerfilLogic } from "./usePerfilLogic";
import TelefonoInput from "../../Components/TelefonoInput";
import { reglaTelefono } from "../../utils/telefonoE164";
import { isAdminRole } from "../../utils/roles.utils";
import {
  CAMPO_CONTRASENA_ACTUAL,
  MENSAJE_CONTRASENA_REQUERIDA,
  cambiaElCorreo,
  correoEscritoEsValido,
} from "../../utils/cambioDeCorreoPropio";
import { despejarDeBarraFija, irAlPrimerError } from "../../utils/formularioErrores";
import { formatRut } from "../../utils/formatRut";
import { passwordMinRule } from "../../utils/passwordPolicy";
import { usersApi } from "../../services/users.api";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import multiavatar from "@multiavatar/multiavatar";
import { AvatarPickerModal } from "./avatarPicker";
import SectoresSoloLectura from "./SectoresSoloLectura";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const ROLE_LABEL: Record<string, string> = {
  superadministrador: "Superadmin",
  administrador: "Administrador",
  aseador: "Aseador",
  // E1 (round 2026-06-20): el cliente CON login es "Cliente" (gestor de la
  // propiedad, ve /finanzas-cliente). El "Propietario" es el dueño legal sin
  // login. Antes ambos se mostraban como "Propietario" — confuso. Ver D-A.
  cliente: "Cliente",
  propietario: "Propietario",
};

const roleChipTone = (role: string): string => {
  if (role === "superadministrador") return "chip-tone-red";
  if (role === "administrador") return "chip-tone-violet";
  if (role === "aseador") return "chip-tone-orange";
  return "chip-tone-blue";
};

const computePasswordStrength = (password: string): { score: number; label: string; color: string } => {
  if (!password) return { score: 0, label: "", color: "#CBD5E1" };
  let score = 0;
  if (password.length >= 6) score += 20;
  if (password.length >= 10) score += 20;
  if (/[A-Z]/.test(password)) score += 20;
  if (/[0-9]/.test(password)) score += 20;
  if (/[^A-Za-z0-9]/.test(password)) score += 20;
  if (score <= 20) return { score, label: "Débil", color: "#EF4444" };
  if (score <= 60) return { score, label: "Media", color: "#F59E0B" };
  if (score <= 80) return { score, label: "Buena", color: "#10B981" };
  return { score, label: "Excelente", color: "#059669" };
};

const Perfil: React.FC = () => {
  const {
    userData,
    isAseador,
    form,
    availableBanks,
    accountTypes,
    handleUpdate,
    toggleSidebar,
    sidebarOpen,
    avatarSeed,
    avatarUrl,
    avatarType,
    avatarFile,
    mostrarModal,
    openAvatarModal,
    closeAvatarModal,
    persistAvatar,
    savingAvatar,
  } = usePerfilLogic();

  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [saving, setSaving] = useState(false);
  const [passwordForm] = Form.useForm();
  const [savingPassword, setSavingPassword] = useState(false);
  const [initialValues, setInitialValues] = useState<Record<string, any> | null>(null);

  const watchedValues = Form.useWatch([], form);
  const watchedPassword = Form.useWatch("newPassword", passwordForm) ?? "";

  // Round 2026-04-30 octies — fix bug "el banner 'Tienes cambios sin
  // guardar' aparece siempre y nunca se va". Causa: el JSON.stringify
  // directo comparaba `watchedValues` (solo los Form.Item realmente
  // renderizados) contra `initialValues` (los 9 campos siempre seteados).
  // Para usuarios NO-aseadores los campos `rut/sector/banco/tipocuenta/
  // numerocuenta` viven adentro de `{isAseador && (...)}` y nunca se
  // registran en el form, así que `useWatch` devolvía solo 4 keys mientras
  // `initialValues` tenía 9 → mismatch permanente.
  //
  // Fix: normalizar ambos lados a un shape canónico con defaults estables
  // (string vacío, array vacío, null), tipo-coerced. Empate exacto cuando
  // el usuario no tocó nada — independientemente de qué Form.Items estén
  // montados. Los sectores YA NO entran (round 2026-10-28): son solo lectura,
  // los asigna la administración.
  const normalizeForCompare = (v: any) => ({
    nombre: String(v?.nombre ?? "").trim(),
    email: String(v?.email ?? "").trim(),
    telefono: String(v?.telefono ?? "").trim(),
    descripcion: String(v?.descripcion ?? "").trim(),
    rut: String(v?.rut ?? "").trim(),
    banco: v?.banco ?? null,
    tipocuenta: v?.tipocuenta ?? null,
    numerocuenta: String(v?.numerocuenta ?? "").trim(),
  });

  const hasChanges = useMemo(() => {
    if (!initialValues || !watchedValues) return false;
    return (
      JSON.stringify(normalizeForCompare(watchedValues)) !==
      JSON.stringify(normalizeForCompare(initialValues))
    );
  }, [watchedValues, initialValues]);

  const avatarSrc =
    avatarType === "upload"
      ? avatarFile
        ? URL.createObjectURL(avatarFile)
        : undefined
      : undefined;

  useEffect(() => {
    if (userData) {
      const values = {
        nombre: userData.nombre_usuario ?? "",
        email: userData.email_usuario ?? "",
        telefono: userData.telefono_usuario ?? "",
        descripcion: userData.descripcion ?? "",
        rut: userData.rut_quien_recibe ?? "",
        banco: userData.banco_quien_recibe ?? undefined,
        tipocuenta: userData.tipocuenta_quien_recibe ?? undefined,
        numerocuenta: userData.numerocuenta_quien_recibe ?? "",
      };
      form.setFieldsValue(values);
      setInitialValues(values);
    }
  }, [userData, form]);

  const handleSave = async () => {
    setSaving(true);
    setAlertMessage(null);
    try {
      // El avatar ya se persiste al confirmar el picker (persistAvatar), así
      // que "Guardar cambios" solo actualiza los campos del formulario.
      const resultado = await handleUpdate();
      // Si el servidor rechazó el cambio (teléfono repetido, correo ya usado, contraseña actual incorrecta, error) o no aplicó lo
      // único que se pidió, NO se anuncia el éxito ni se apaga la barra de «cambios sin guardar»: lo escrito se conserva para
      // corregirlo. El motivo ya lo mostró el aviso de la llamada y, si es del teléfono o de la contraseña, el propio campo.
      // "reingreso": el correo cambió y la sesión se cerró; el hook ya avisó y no hay nada más que mostrar en esta pantalla.
      if (resultado === "fallo" || resultado === "no_aplicado" || resultado === "reingreso") return;
      // Solo "guardado" anuncia el éxito. En "parcial" y "sin_cambios" el hook ya dijo UNA vez qué pasó (y no hay nada más que
      // guardar), así que la barra se apaga igual pero sin un cartel verde que contradiga ese aviso.
      if (resultado === "guardado") {
        setAlertType("success");
        setAlertMessage("Perfil actualizado correctamente");
      }
      // Round 2026-04-30 octies — usar getFieldsValue(true) para incluir
      // TODOS los campos seteados con setFieldsValue, incluso si su
      // Form.Item no está montado (usuario no-aseador no ve los campos
      // bancarios pero igual fueron iniciados). Sin `true` el banner
      // volvía a aparecer post-save porque getFieldsValue() filtraba
      // los unmounted.
      // La contraseña actual (si se pidió) nunca pasa a un estado de la página: se descarta de la base de comparación.
      const base = { ...form.getFieldsValue(true) };
      delete base[CAMPO_CONTRASENA_ACTUAL];
      setInitialValues(base);
    } catch (err: any) {
      setAlertType("error");
      setAlertMessage(err?.message || "Error al guardar perfil");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
    // Lo que se escribió en «Contraseña actual» no sobrevive a un «Descartar».
    form.setFieldValue(CAMPO_CONTRASENA_ACTUAL, undefined);
    setAlertMessage(null);
  };

  // Rol escalar post-refactor. `visibleRoles` queda como array de un elemento
  // para no romper el render actual.
  const rol = Array.isArray(userData?.rol)
    ? (userData.rol as unknown as string[])[0] ?? ""
    : ((userData?.rol as unknown as string) ?? "");
  const visibleRoles = rol && rol !== "usuario" && rol !== "otro" ? [rol] : [];
  const canEditRut = rol === "administrador" || rol === "superadministrador";
  // El correo propio cambió (a ojos del servidor: sin espacios ni mayúsculas) Y lo escrito ya es un correo completo: SOLO entonces
  // se pide la contraseña actual. Con el correo vacío o a medio escribir primero hay que arreglar el correo (ronda F3b): pedir la
  // contraseña y mostrar su mensaje rojo en ese momento era exigir dos cosas a la vez.
  const cambiaCorreo =
    !!userData &&
    watchedValues?.email !== undefined &&
    correoEscritoEsValido(watchedValues.email) &&
    cambiaElCorreo(userData.email_usuario, watchedValues.email);
  // «Contraseña actual» NACE (al volverse válido el correo) en la parte de abajo de la pantalla, donde está la barra «Tienes cambios sin
  // guardar»: a 1366x768 quedaba entera bajo ella. Si la tapa, se sube SIN quitarle el foco al correo que la persona está escribiendo
  // (ronda F3b: el campo y su mensaje tienen que estar siempre a la vista).
  useEffect(() => {
    if (cambiaCorreo) despejarDeBarraFija(document.getElementById(CAMPO_CONTRASENA_ACTUAL));
  }, [cambiaCorreo]);
  // El asistente de WhatsApp atiende SOLO a administración (decisión del dueño): a una aseadora o a un cliente no le toca ese aviso.
  const avisoTelefonoVacio = !userData?.telefono_usuario
    ? undefined
    : isAdminRole(rol)
      ? "Si dejas el teléfono vacío, el asistente de WhatsApp no podrá reconocerte."
      : "Sin teléfono no podremos contactarte.";
  const passwordStrength = computePasswordStrength(watchedPassword);

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="home-content"
    >
      <div className="perfil-container">
        <PageHeader
          title="Mi Perfil"
          helpText="Administra tu información personal, datos de contacto y seguridad."
        />

        {alertMessage && (
          <Alert
            message={alertMessage}
            type={alertType}
            showIcon
            closable
            onClose={() => setAlertMessage(null)}
            style={{ marginBottom: 16, maxWidth: 860, margin: "0 auto 16px" }}
          />
        )}

        {/* ── Hero ── */}
        <Card className="perfil-hero">
          <div className="hero-content">
            <div className="avatar-wrapper" onClick={openAvatarModal}>
              {avatarUrl ? (
                <img src={avatarUrl} className="avatar-img" alt="avatar" />
              ) : avatarSrc ? (
                <img src={avatarSrc} className="avatar-img" alt="avatar" />
              ) : (
                <div
                  className="avatar-svg"
                  dangerouslySetInnerHTML={{ __html: multiavatar(avatarSeed) }}
                />
              )}
              <span className="avatar-edit-fab" aria-label="Cambiar avatar">
                <CameraOutlined />
              </span>
            </div>

            <div className="hero-meta">
              <Title level={3} className="hero-name">
                {userData?.nombre_usuario || "Usuario"}
              </Title>
              <Space size={4} className="hero-email" align="center">
                <MailOutlined />
                <Text type="secondary">{userData?.email_usuario || "—"}</Text>
              </Space>
              <Space size={6} wrap className="hero-chips">
                {visibleRoles.length > 0 ? (
                  visibleRoles.map((r: string) => (
                    <span key={r} className={`perfil-chip ${roleChipTone(r)}`}>
                      <CheckCircleFilled />
                      {ROLE_LABEL[r] ?? r}
                    </span>
                  ))
                ) : (
                  <span className="perfil-chip chip-tone-muted">Usuario</span>
                )}
                {isAseador && Array.isArray(userData?.sector) && userData.sector.length > 0 && (
                  <span className="perfil-chip chip-tone-muted">
                    <EnvironmentOutlined />
                    {userData.sector.length === 1
                      ? userData.sector[0]
                      : `${userData.sector.length} sectores`}
                  </span>
                )}
              </Space>
            </div>
          </div>
        </Card>

        {mostrarModal && (
          <AvatarPickerModal
            initialSeed={avatarSeed}
            initialUrl={avatarUrl ?? (avatarType === "upload" ? avatarSrc : undefined)}
            onClose={closeAvatarModal}
            onConfirm={persistAvatar}
            saving={savingAvatar}
          />
        )}

        {/* ── Form ── */}
        {/* «Guardar cambios» es el botón de envío del formulario: así Enter dentro de un campo guarda (ronda F3b; antes solo servía el
            clic). `onFinish` corre con todo válido; si no, `onFinishFailed` lleva a la persona al primer campo con error. */}
        <Form
          layout="vertical"
          form={form}
          className="perfil-form"
          onFinish={() => {
            void handleSave();
          }}
          onFinishFailed={(info) => {
            irAlPrimerError(form, info);
          }}
        >
          {/* Datos personales */}
          <Card className="perfil-card">
            <div className="card-header">
              <div>
                <Title level={5} className="card-title">
                  <UserOutlined /> Datos personales
                </Title>
                <Text type="secondary">Tu información básica de contacto.</Text>
              </div>
            </div>

            <div className="form-grid">
              <Form.Item
                name="nombre"
                label="Nombre completo"
                rules={[{ required: true, message: "Ingresa tu nombre" }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Tu nombre" />
              </Form.Item>

              {/* Teléfono con código de país obligatorio (el servidor exige la misma forma). Es el de QUIEN USA la pantalla: por eso
                  el navegador puede sugerirlo (`propio`). Un dato antiguo sin código que no se toca no impide guardar el resto. */}
              <Form.Item
                name="telefono"
                label={
                  <Space size={4}>
                    <PhoneOutlined />
                    Teléfono
                  </Space>
                }
                rules={[reglaTelefono({ valorInicial: userData?.telefono_usuario })]}
              >
                <TelefonoInput propio ariaLabel="Tu teléfono" avisoSiVacio={avisoTelefonoVacio} />
              </Form.Item>

              <Form.Item
                name="email"
                label={
                  <Space size={4}>
                    Correo electrónico
                    <Tooltip title="Este correo es el que usas para iniciar sesión.">
                      <InfoCircleOutlined style={{ color: "var(--text-muted)" }} />
                    </Tooltip>
                  </Space>
                }
                rules={[
                  { required: true, message: "Ingresa tu correo" },
                  { type: "email", message: "Correo no válido" },
                ]}
                className="form-full"
              >
                <Input prefix={<MailOutlined />} placeholder="correo@ejemplo.com" />
              </Form.Item>

              {/* Cambiar el correo de TU cuenta pide la contraseña actual (S-03): con una sesión robada, sin la contraseña, no se puede
                  tomar la cuenta. Aparece SOLO cuando el correo cambió; `preserve={false}` borra lo escrito si se devuelve el correo al
                  original. Vive solo en el formulario (memoria): no se guarda en ningún estado persistente ni en localStorage. */}
              {cambiaCorreo && (
                <Form.Item
                  name={CAMPO_CONTRASENA_ACTUAL}
                  label="Contraseña actual"
                  className="form-full"
                  preserve={false}
                  extra="Por seguridad, para cambiar tu correo confirma tu contraseña actual. Te avisaremos al correo anterior."
                  rules={[{ required: true, message: MENSAJE_CONTRASENA_REQUERIDA }]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Tu contraseña actual"
                    aria-label="Contraseña actual, para confirmar el cambio de correo"
                    autoComplete="current-password"
                  />
                </Form.Item>
              )}

              <Form.Item
                name="descripcion"
                label="Descripción"
                className="form-full"
              >
                <Input.TextArea
                  rows={3}
                  placeholder="Cuéntanos algo sobre ti (opcional)"
                  showCount
                  maxLength={280}
                />
              </Form.Item>
            </div>
          </Card>

          {isAseador && (
            <>
              {/* Información laboral */}
              <Card className="perfil-card">
                <div className="card-header">
                  <div>
                    <Title level={5} className="card-title">
                      <EnvironmentOutlined /> Información laboral
                    </Title>
                    <Text type="secondary">
                      Tus sectores de trabajo y tu identificación fiscal.
                    </Text>
                  </div>
                </div>

                <div className="form-grid">
                  <Form.Item
                    name="rut"
                    label={
                      <Space size={4}>
                        RUT
                        {!canEditRut && (
                          <Tooltip title="Solo un administrador puede modificar tu RUT.">
                            <InfoCircleOutlined style={{ color: "var(--text-muted)" }} />
                          </Tooltip>
                        )}
                      </Space>
                    }
                  >
                    <Input
                      prefix={<IdcardOutlined />}
                      placeholder="12.345.678-9"
                      disabled={!canEditRut}
                      onChange={(e) =>
                        form.setFieldsValue({ rut: formatRut(e.target.value) })
                      }
                    />
                  </Form.Item>

                  <div /> {/* grid spacer */}

                  {/* Sectores: SOLO LECTURA (round 2026-10-28). Los asigna la administración
                      desde /aseadores; el backend ignora el campo si lo manda una aseadora. */}
                  <SectoresSoloLectura sectores={userData?.sector} />
                </div>
              </Card>

              {/* Datos bancarios */}
              <Card className="perfil-card">
                <div className="card-header">
                  <div>
                    <Title level={5} className="card-title">
                      <BankOutlined /> Datos bancarios
                    </Title>
                    <Text type="secondary">
                      Información para recibir tus pagos. Se guarda cifrada.
                    </Text>
                  </div>
                </div>

                <div className="form-grid">
                  <Form.Item
                    name="banco"
                    label="Banco"
                    rules={[{ required: Boolean(userData?.banco_quien_recibe), message: "Selecciona un banco" }]}
                  >
                    <Select placeholder="Selecciona un banco">
                      {availableBanks.map((bank: any) => (
                        <Option key={bank} value={bank}>
                          {bank}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="tipocuenta"
                    label="Tipo de cuenta"
                    rules={[{ required: Boolean(userData?.tipocuenta_quien_recibe), message: "Selecciona el tipo de cuenta" }]}
                  >
                    <Select placeholder="Selecciona un tipo de cuenta">
                      {accountTypes.map((type: any) => (
                        <Option key={type} value={type}>
                          {type}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="numerocuenta"
                    label="Número de cuenta"
                    className="form-full"
                  >
                    <Input prefix={<CreditCardOutlined />} placeholder="Número de cuenta" />
                  </Form.Item>
                </div>
              </Card>
            </>
          )}

          {/* Barra flotante de cambios. `data-barra-fija`: el desplazamiento al campo con error la esquiva (ver `llevarCampoALaVista`). */}
          {hasChanges && (
            <div className="unsaved-bar" data-barra-fija="">
              <Text className="unsaved-bar-text">
                <InfoCircleOutlined /> Tienes cambios sin guardar
              </Text>
              <Space>
                <Button onClick={handleCancel} disabled={saving}>
                  Descartar
                </Button>
                <Button type="primary" htmlType="submit" loading={saving}>
                  Guardar cambios
                </Button>
              </Space>
            </div>
          )}
        </Form>

        {/* ── Seguridad ── */}
        <Card className="perfil-card">
          <div className="card-header">
            <div>
              <Title level={5} className="card-title">
                <LockOutlined /> Seguridad
              </Title>
              <Text type="secondary">Cambia tu contraseña periódicamente.</Text>
            </div>
          </div>

          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={async (values) => {
              if (!userData?.id) return;
              setSavingPassword(true);
              try {
                const res = await usersApi.changePassword(
                  userData.id,
                  values.currentPassword,
                  values.newPassword,
                );
                if (res) {
                  passwordForm.resetFields();
                  // El backend invalida el JWT actual al cambiar la contraseña
                  // (token_version incrementa). Forzamos logout local para
                  // evitar que el siguiente request caiga como 401 "Sesión
                  // invalidada" y el usuario se quede mirando errores.
                  getMsg()?.success(
                    "Contraseña actualizada. Vuelve a iniciar sesión.",
                  );
                  // Pequeño delay para que el mensaje sea visible
                  setTimeout(() => {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    sessionStorage.removeItem("token");
                    sessionStorage.removeItem("user");
                    window.location.href = "/login";
                  }, 1200);
                }
              } catch (err: any) {
                getMsg()?.error(err?.message || "Error al cambiar contraseña");
              } finally {
                setSavingPassword(false);
              }
            }}
          >
            <div className="form-grid">
              <Form.Item
                name="currentPassword"
                label="Contraseña actual"
                rules={[
                  { required: true, message: "Ingresa tu contraseña actual" },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Tu contraseña actual"
                  autoComplete="current-password"
                />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label="Nueva contraseña"
                rules={[
                  { required: true, message: "Ingresa la nueva contraseña" },
                  passwordMinRule,
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Nueva contraseña"
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirmar contraseña"
                dependencies={["newPassword"]}
                rules={[
                  { required: true, message: "Confirma la contraseña" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("newPassword") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Las contraseñas no coinciden"));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Repite la contraseña" />
              </Form.Item>

              {watchedPassword && (
                <div className="form-full password-strength">
                  <div className="password-strength-head">
                    <Text style={{ fontSize: 12 }}>Fuerza de la contraseña</Text>
                    <Text strong style={{ fontSize: 12, color: passwordStrength.color }}>
                      {passwordStrength.label}
                    </Text>
                  </div>
                  <Progress
                    percent={passwordStrength.score}
                    showInfo={false}
                    strokeColor={passwordStrength.color}
                    size="small"
                  />
                  <Paragraph type="secondary" className="password-hint">
                    Usa al menos 10 caracteres, con mayúsculas, números y símbolos.
                  </Paragraph>
                </div>
              )}

              <Form.Item className="form-full" style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" loading={savingPassword}>
                  Cambiar contraseña
                </Button>
              </Form.Item>
            </div>
          </Form>
        </Card>
      </div>
    </Layout>
  );
};

export default Perfil;
